import React, { useState } from 'react';
import { Folder, GripVertical, Play, Video } from 'lucide-react';
import type { HandbookTrainingTreeNode } from '../../../utils/handbookTrainingTree';
import { isHandbookTrainingVideoNew } from '../../../utils/handbookTrainingAccess';

const DRAG_MIME = 'application/x-lctf-handbook-training-node';

type Props = {
  videos: HandbookTrainingTreeNode[];
  subfolders: HandbookTrainingTreeNode[];
  watchedNodeIds: ReadonlySet<string>;
  canManage?: boolean;
  onSelectVideo: (nodeId: string) => void;
  onSelectFolder: (nodeId: string) => void;
  onReorderVideo?: (draggedId: string, targetId: string, insertBefore: boolean) => void;
};

const HandbookTrainingFolderCards: React.FC<Props> = ({
  videos,
  subfolders,
  watchedNodeIds,
  canManage = false,
  onSelectVideo,
  onSelectFolder,
  onReorderVideo,
}) => {
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [dropBefore, setDropBefore] = useState(true);

  if (!videos.length && !subfolders.length) {
    return <p className="people-hub-muted">No videos in this section yet.</p>;
  }

  const readDragId = (e: React.DragEvent) => {
    const id = e.dataTransfer.getData(DRAG_MIME) || e.dataTransfer.getData('text/plain');
    return id.trim() || null;
  };

  return (
    <div className="people-handbook-training-folder-cards">
      {subfolders.length > 0 && (
        <div className="people-handbook-training-folder-cards-section">
          <h4 className="people-handbook-training-folder-cards-heading">Topics</h4>
          <div className="people-handbook-training-folder-cards-grid">
            {subfolders.map(folder => (
              <button
                key={folder.id}
                type="button"
                className="people-handbook-training-folder-card people-handbook-training-folder-card-subfolder"
                onClick={() => onSelectFolder(folder.id)}
              >
                <Folder size={18} aria-hidden className="people-handbook-training-folder-card-icon" />
                <span className="people-handbook-training-folder-card-title">{folder.title}</span>
                {folder.description && (
                  <span className="people-handbook-training-folder-card-desc">{folder.description}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      {videos.length > 0 && (
        <div className="people-handbook-training-folder-cards-section">
          {subfolders.length > 0 && (
            <h4 className="people-handbook-training-folder-cards-heading">Videos</h4>
          )}
          {canManage && onReorderVideo && videos.length > 1 && (
            <p className="people-handbook-training-organize-hint">
              Drag video cards by the handle to change order in this folder.
            </p>
          )}
          <div className="people-handbook-training-folder-cards-grid">
            {videos.map(video => {
              const isNew = isHandbookTrainingVideoNew(video, watchedNodeIds);
              const isDrop = dropTargetId === video.id;
              return (
                <div
                  key={video.id}
                  className={`people-handbook-training-folder-card-wrap ${
                    isDrop ? (dropBefore ? 'drop-before' : 'drop-after') : ''
                  }`}
                  onDragOver={
                    canManage && onReorderVideo
                      ? e => {
                          e.preventDefault();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setDropBefore(e.clientY < rect.top + rect.height / 2);
                          setDropTargetId(video.id);
                        }
                      : undefined
                  }
                  onDragLeave={() => setDropTargetId(null)}
                  onDrop={
                    canManage && onReorderVideo
                      ? e => {
                          e.preventDefault();
                          const draggedId = readDragId(e);
                          setDropTargetId(null);
                          if (!draggedId || draggedId === video.id) return;
                          const rect = e.currentTarget.getBoundingClientRect();
                          const before = e.clientY < rect.top + rect.height / 2;
                          onReorderVideo(draggedId, video.id, before);
                        }
                      : undefined
                  }
                >
                  <div className="people-handbook-training-folder-card people-handbook-training-folder-card-video">
                    {canManage && onReorderVideo && (
                      <span
                        className="people-handbook-training-folder-card-drag"
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.setData(DRAG_MIME, video.id);
                          e.dataTransfer.setData('text/plain', video.id);
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        title="Drag to reorder"
                      >
                        <GripVertical size={16} aria-hidden />
                      </span>
                    )}
                    <button
                      type="button"
                      className="people-handbook-training-folder-card-main"
                      onClick={() => onSelectVideo(video.id)}
                    >
                      <span className="people-handbook-training-folder-card-thumb" aria-hidden>
                        <Video size={22} />
                        <Play size={14} className="people-handbook-training-folder-card-play" />
                      </span>
                      <span className="people-handbook-training-folder-card-body">
                        <span className="people-handbook-training-folder-card-title-row">
                          <span className="people-handbook-training-folder-card-title">{video.title}</span>
                          {isNew && <span className="people-handbook-training-tree-new">New</span>}
                          {!video.published && (
                            <span className="people-handbook-training-tree-draft">Draft</span>
                          )}
                        </span>
                        {video.description && (
                          <span className="people-handbook-training-folder-card-desc">{video.description}</span>
                        )}
                      </span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default HandbookTrainingFolderCards;
